import { SALUTATIONS } from '../../../../lib/salutations';
import { uid } from '../../../../lib/uid';
import type { FamilyRelation } from '../../family-relations/familyRelationTypes';

/**
 * Test data backing the Autogenerate button on the family form.
 *
 * **The names here are deliberately NOT names**, and this is the generator CLAUDE.md names in
 * advance: a family member is written into `rsys_pii`, the one store whose entire purpose is
 * holding real people's identities. A fabricated-but-realistic name sitting in that table is
 * indistinguishable from a real one to anyone auditing it later, and "is this row a person?" is
 * the question that store exists to make answerable.
 *
 * So the values read as obviously synthetic on sight — the same rule `samplePersonnel` follows,
 * and the opposite of `sampleEducation`, whose school name never leaves `rsys_`.
 *
 * **It takes the CATALOG, and that is not optional.** The relation became a required field when
 * it stopped being an enum, so a generator that did not fill one would leave Save disabled —
 * which is precisely the silent breakage CLAUDE.md warns about, because nothing fails and the
 * button simply stops working. It also has to resolve against the rows the form actually
 * loaded: an id this function invented would fail the server's "it must exist and be active"
 * check, and that reads as a bug in the form.
 *
 * Reading the chosen row's flags is what keeps it honest against a relation an administrator
 * added: a generator that special-cased the four seeded names would fill nothing for "Guardian".
 *
 * Keep it in lockstep with the form: a new required field there without a value here silently
 * breaks the button, and nothing fails.
 */
export function sampleFamilyMember(
  /** The active relations the form loaded. Empty only while the fetch is in flight or failed. */
  relations: FamilyRelation[],
  /** What the reader has already picked, if anything. */
  chosen: FamilyRelation | null,
): {
  relationId: string;
  salutation: string;
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  dateOfBirth: string;
  occupation: string;
  employerName: string;
  businessAddress: string;
  telephoneNumber: string;
} {
  const tag = uid('').slice(0, 6);

  /*
    The reader's own choice wins — "never a field the reader has already decided". Otherwise the
    FIRST active relation, which is the catalog's own order rather than a random pick: a
    generator whose output the reader cannot predict is worse than one that fills less.
  */
  const relation = chosen ?? relations[0] ?? null;

  /*
    Filled whenever the relation requires one, because the server refuses the save without it
    and the form marks the field required — a generator that left it blank would produce a form
    that cannot be submitted, which is the one thing Autogenerate exists to prevent.

    Dated plainly in the past: a window computed from `now` eventually drifts past the max the
    field allows.
  */
  const dateOfBirth = relation?.requiresDateOfBirth ? '2015-04-12' : '';

  /*
    The occupation block is drawn only where the relation asks for it. Filling those four for a
    relation that does not would put values on screen the form never shows, which is a worse
    test than leaving them empty.
  */
  const occupational = relation?.collectsOccupation ?? false;

  return {
    relationId: relation?.id ?? '',
    salutation: SALUTATIONS[Math.floor(Math.random() * SALUTATIONS.length)],
    firstName: 'Placeholder',
    middleName: 'Testcase',
    lastName: `Sample-${tag.toUpperCase()}`,
    suffix: '',
    dateOfBirth,
    occupation: occupational ? 'Placeholder occupation' : '',
    employerName: occupational ? 'Example Employer (test)' : '',
    businessAddress: occupational ? 'Sample Address, Testville' : '',

    // The reserved fiction block, so a generated number can never reach a real handset.
    telephoneNumber: occupational ? '+63 917 555 0142' : '',
  };
}
